// src/private/scenarioCardGrid.js
// scenari_futuri layout math, pulled out of InfiniteCanvas.js so it's
// unit-testable on its own — InfiniteCanvas.js transitively imports
// firebase/auth (via AuthContext), which breaks under jsdom, so nothing
// defined directly in that file can be imported by a Jest test.
//
// The area is a T: the scenario tree spans the full width up top; below
// it, a left column for mentor-placed cards and a right column for
// mentor-authored notes, side by side — kept as two separate columns
// (rather than interleaving both kinds in one flow) so a burst of cards
// doesn't shove that turn's notes far down the page, and vice versa.
//
// Neither this mode nor mappatura renders kanban column backgrounds (see
// ZoneLayer.js), so there's nothing for columnOf()'s kanban-bucket counting
// to align to, and the old note placement (ZONE_BASE_POSITIONS-based) was
// equally meaningless here — both used to either stack on top of each
// other or land at some arbitrary kanban-zone coordinate that isn't even
// rendered in this mode. Both columns flow left-to-right, wrapping to a
// new row once a column's fixed usable width fills up.
import { AREA_BOUNDS } from "./dtZones";
import { TIMELINE_ORIGIN_OFFSET } from "./ScenarioTimeline";
import { SCENARIO_MIN_W } from "./ZoneLayer";
import { CARD_W, CARD_H } from "./MagicCursor";

// ---- shared T-layout geometry ----

export const SCENARIO_LAYOUT_MARGIN = 80; // left/right edge margin of the whole area
export const SCENARIO_COLUMN_GAP = 100; // gap between the cards column and the notes column
// Minimum clearance between the tree's own lowest content (root/branch
// pills, matrix-numbering chips — all included in ScenarioTimeline's own
// measured size, see ZoneLayer.js's scenarioSize) and the top of the row
// below it. Sizing the columns against SCENARIO_MIN_W (rather than the
// live, tree-driven brainstormWidth plumbed down from ZoneLayer) is a
// deliberate simplification: the columns only ever need to fit within
// SCENARIO_MIN_W's worth of room, so any extra width from a wide tree is
// just unused margin to the right, never a wrap that happens too early.
export const SCENARIO_TREE_ROW_GAP = 120;

const USABLE_W = SCENARIO_MIN_W - SCENARIO_LAYOUT_MARGIN * 2;
const COLUMN_W = (USABLE_W - SCENARIO_COLUMN_GAP) / 2;
export const SCENARIO_CARDS_COLUMN_X = AREA_BOUNDS.brainstorm.x + SCENARIO_LAYOUT_MARGIN;
export const SCENARIO_NOTES_COLUMN_X = SCENARIO_CARDS_COLUMN_X + COLUMN_W + SCENARIO_COLUMN_GAP;

// Canvas-space top-left corner the tree itself renders from (mirrors
// ScenarioTimeline.jsx's own originX/originY) — exported so a caller that
// needs to frame the tree (e.g. a camera zoom onto a newly-proposed fork)
// doesn't need to re-import/duplicate these constants from
// ScenarioTimeline.jsx directly.
export const SCENARIO_TREE_ORIGIN_X = AREA_BOUNDS.brainstorm.x + 60;
export const SCENARIO_TREE_ORIGIN_Y = AREA_BOUNDS.brainstorm.y + TIMELINE_ORIGIN_OFFSET;

// Minimum size to frame when the camera zooms onto the tree for a newly-
// proposed fork — a just-started tree (root only) is tiny, and framing
// only its exact measured size would zoom in uncomfortably tight on a
// single pill.
const SCENARIO_TREE_CAMERA_MIN_W = 1200;
const SCENARIO_TREE_CAMERA_MIN_H = 500;

// fitBounds-ready rect covering the tree's current live measured size
// (ZoneLayer's scenarioSize, mirrored up to InfiniteCanvas). Deliberately
// generous rather than pixel-tight around the exact new node: the size
// measurement can be one render-cycle stale relative to a fork that was
// just added (ScenarioTimeline reports its new size only after the new
// branch actually renders), and the newly-proposed node's own "in attesa"
// styling already makes it visually distinct once framed — exact
// per-node targeting isn't needed for "zoom onto the fork" to read
// correctly, and would need ScenarioTimeline to report individual node
// positions, which it doesn't today.
export function scenarioTreeCameraBounds(treeSize) {
  return {
    x: SCENARIO_TREE_ORIGIN_X - 60,
    y: SCENARIO_TREE_ORIGIN_Y - 60,
    width: Math.max(SCENARIO_TREE_CAMERA_MIN_W, (treeSize?.width || 0) + 120),
    height: Math.max(SCENARIO_TREE_CAMERA_MIN_H, (treeSize?.height || 0) + 120),
  };
}

// The tree-height=0 floor for the row below the tree — also doubles as the
// permissive lower bound isInScenarioCardGrid/isInScenarioNoteGrid use, see
// their own comments for why that bound deliberately does NOT track the
// tree's current (possibly grown-since) height.
const SCENARIO_ROW_MIN_ORIGIN_Y = AREA_BOUNDS.brainstorm.y + TIMELINE_ORIGIN_OFFSET;

// Where the row below the tree should start, given the tree's current live
// measured height (ZoneLayer's scenarioSize.height, mirrored up to
// InfiniteCanvas via onScenarioTreeSizeChange) — guarantees at least
// SCENARIO_TREE_ROW_GAP of clearance below whatever the tree currently
// occupies. An empty/not-yet-measured tree (height 0) needs no gap to
// clear, so it sits exactly at the floor rather than the floor-plus-gap —
// the gap only exists to clear actual tree content. Shared by both the
// cards column and the notes column: they sit side by side, at the same
// height.
//
// Known ceiling: this places NEW cards/notes correctly relative to the
// tree's height AT PLACEMENT TIME. If the tree grows taller on a LATER
// turn (another fork added after cards/notes already landed), already-
// placed items are not retroactively pushed further down — only freshly-
// placed ones get the up-to-date gap. Full reflow (shifting existing
// nodes when the tree grows past them) would need its own dedicated pass;
// not built here.
export function scenarioBottomRowOriginY(treeHeight = 0) {
  const clampedHeight = Math.max(0, treeHeight);
  const treeBottom = SCENARIO_ROW_MIN_ORIGIN_Y + clampedHeight;
  return clampedHeight > 0 ? treeBottom + SCENARIO_TREE_ROW_GAP : treeBottom;
}

// ---- cards (bottom-left) ----

export const SCENARIO_CARD_GRID_GAP = 40;
export const SCENARIO_CARD_GRID_PER_ROW = Math.max(
  1, Math.floor(COLUMN_W / (CARD_W + SCENARIO_CARD_GRID_GAP))
);

// The Nth card's (0-based) slot in the flowing grid, below `originY` (see
// scenarioBottomRowOriginY — pass the tree-height-aware value when placing
// new cards).
export function scenarioCardGridSlot(index, originY = SCENARIO_ROW_MIN_ORIGIN_Y) {
  const row = Math.floor(index / SCENARIO_CARD_GRID_PER_ROW);
  const col = index % SCENARIO_CARD_GRID_PER_ROW;
  return {
    x: SCENARIO_CARDS_COLUMN_X + col * (CARD_W + SCENARIO_CARD_GRID_GAP),
    y: originY + row * (CARD_H + SCENARIO_CARD_GRID_GAP),
  };
}

// A node already sitting in the cards column (placed by a prior magic-
// cursor flight, or left there after a manual drag) — used both to seed
// the next slot index (continues the flow across turns instead of
// restarting at 0 and re-stacking on turn 1's cards) and to measure how
// tall the column currently is (for the area's live auto-grow/shrink
// height).
//
// Deliberately checked against the permissive tree-height=0 floor, NOT
// the current (possibly tree-grown-since) placement origin — see
// scenarioBottomRowOriginY's own comment for why.
export function isInScenarioCardGrid(node) {
  return node.type === "customCardNode"
    && node.position.x >= SCENARIO_CARDS_COLUMN_X - CARD_W
    && node.position.x < SCENARIO_NOTES_COLUMN_X - SCENARIO_COLUMN_GAP / 2
    && node.position.y >= SCENARIO_ROW_MIN_ORIGIN_Y - CARD_H;
}

// Area-local height (relative to AREA_BOUNDS.brainstorm.y) the cards
// column currently occupies, derived live from actual node positions.
// Returns 0 when the column is empty.
export function scenarioCardsGridHeight(nodes) {
  let maxY = -Infinity;
  for (const n of nodes) {
    if (isInScenarioCardGrid(n)) maxY = Math.max(maxY, n.position.y);
  }
  return maxY === -Infinity ? 0 : (maxY - AREA_BOUNDS.brainstorm.y) + CARD_H;
}

// ---- notes (bottom-right) ----

// PostItNode.js's own default size when a note doesn't set data.w/h — see
// its `const width = data.w || 150`. Mentor-authored notes (commitMagicNote
// in InfiniteCanvas.js) never set w/h, so this is the real rendered size.
export const NOTE_W = 150;
export const NOTE_H = 150;
export const SCENARIO_NOTE_GRID_GAP = 30;
export const SCENARIO_NOTE_GRID_PER_ROW = Math.max(
  1, Math.floor(COLUMN_W / (NOTE_W + SCENARIO_NOTE_GRID_GAP))
);

// The Nth note's (0-based) slot in the flowing grid, below `originY` (see
// scenarioBottomRowOriginY).
export function scenarioNoteGridSlot(index, originY = SCENARIO_ROW_MIN_ORIGIN_Y) {
  const row = Math.floor(index / SCENARIO_NOTE_GRID_PER_ROW);
  const col = index % SCENARIO_NOTE_GRID_PER_ROW;
  return {
    x: SCENARIO_NOTES_COLUMN_X + col * (NOTE_W + SCENARIO_NOTE_GRID_GAP),
    y: originY + row * (NOTE_H + SCENARIO_NOTE_GRID_GAP),
  };
}

// Mirrors isInScenarioCardGrid, for the notes column.
export function isInScenarioNoteGrid(node) {
  return node.type === "postItNode"
    && node.position.x >= SCENARIO_NOTES_COLUMN_X - NOTE_W
    && node.position.y >= SCENARIO_ROW_MIN_ORIGIN_Y - NOTE_H;
}

// Mirrors scenarioCardsGridHeight, for the notes column.
export function scenarioNotesGridHeight(nodes) {
  let maxY = -Infinity;
  for (const n of nodes) {
    if (isInScenarioNoteGrid(n)) maxY = Math.max(maxY, n.position.y);
  }
  return maxY === -Infinity ? 0 : (maxY - AREA_BOUNDS.brainstorm.y) + NOTE_H;
}
